<template>
  <div class="accessFirstStep">
    <v-text-field
      data-private
      required
      outlined
      dense
      class="body2 mb-1"
      data-testid="login-input-email"
      :value="account.email"
      :label="`${$t('auth.access.email')}*`"
      :rules="emailRules"
      :placeholder="$t('auth.access.your_email')"
      @input="onChange(FIELDS.email, $event)"
      @keyup.enter="onContinueClick"
    ></v-text-field>

    <v-text-field
      data-private
      class="body2 mb-1"
      data-testid="login-input-password"
      dense
      hide-details
      name="password"
      outlined
      required
      :value="account.password"
      :append-icon="showSeePassword ? 'mdi-eye' : 'mdi-eye-off'"
      :rules="passwordRules"
      :type="showSeePassword ? 'text' : 'password'"
      :label="`${$t('common.users.password')}*`"
      :placeholder="$t('auth.access.enter_your_password')"
      @click:append="showSeePassword = !showSeePassword"
      @input="onChange(FIELDS.password, $event)"
      @keyup.enter="onContinueClick"
    >
    </v-text-field>

    <div class="mt-n2 mb-2 mr-0 d-flex justify-end">
      <Button
        plain
        color="blueGrey"
        data-testid="login-button-resetPassword"
        :text="$t('authManagePeople.forgot_password')"
        @click="$emit('onClickForgotPassword')"
      />
    </div>

    <Button
      block
      data-testid="login-button-continue"
      color="primary"
      :disabled="!isButtonEnabled"
      :text="$t('auth.access.login')"
      @click="onContinueClick"
    />

    <div class="my-10 d-flex align-center justify-space-between">
      <v-divider />
      <span
        class="mx-4 font-weight-medium"
        style="text-transform: uppercase;"
        >{{ $t('common.or') }}</span
      >
      <v-divider />
    </div>

    <v-alert
      v-if="showErrorAlert"
      colored-border
      color="red"
      data-testid="login-alert-invalidCredentials"
      dense
      border="left"
      elevation="2"
      class="text-caption"
    >
      <p
        class="pa-0 ma-0"
        v-html="$sanitize(errorAlertText, { allowedTags: ['br'] })"
      />
    </v-alert>

    <div>
      <v-btn
        block
        class="mb-3 py-3"
        color="blue-grey"
        data-testid="login-button-loginSSO"
        outlined
        rounded
        style="height: 42px;"
        @click="$emit('onSignInWithSSO', $event)"
      >
        <img :src="ssoIcon" alt="sso_icon" class="me-2" />
        <p class="mb-0 blue-grey--text text--darken-2 font-weight-medium">
          {{ $t('auth.access.login_with_sso') }}
        </p>
      </v-btn>

      <v-btn
        block
        class="mb-3 py-3"
        color="blue-grey"
        data-testid="login-button-withGoogle"
        outlined
        rounded
        style="height: 42px;"
        @click="$emit('onSignInWithGoogle', $event)"
      >
        <img :src="googleIcon" alt="google_icon" class="me-2" />
        <p class="mb-0 font-weight-medium blue-grey--text text--darken-2">
          {{ $t('auth.access.login_with_google') }}
        </p>
      </v-btn>

      <v-btn
        block
        class="mb-3 py-3"
        color="blue-grey"
        data-testid="loginActiveDirectory-button-login"
        outlined
        rounded
        style="height: 42px;"
        @click="$emit('onSignInWithMicrosoft', $event)"
      >
        <img :src="microsoftIcon" alt="microsoft_icon" class="me-2" />
        <p class="mb-0 blue-grey--text text--darken-2 font-weight-medium">
          {{ $t('auth.access.login_with_microsoft') }}
        </p>
      </v-btn>

      <Button
        block
        class="mt-15"
        color="primary"
        href="https://help.reverscore.net/en/article/different-types-of-login-xvhmlg/"
        plain
        target="__blank"
        :text="$t('auth.access.need_help')"
      />
    </div>
  </div>
</template>

<script>
import validationHelper from '@/lib/validationHelper.js'

import microsoftIcon from '@/assets/images/icons/icon-microsoft.svg'
import googleIcon from '@/assets/images/icons/icon-google.svg'
import ssoIcon from '@/assets/images/icons/login-sso.svg'
import Button from '@/components/UI/Button'

const FIELDS = {
  email: 'email',
  password: 'password',
}

export default {
  props: {
    account: {
      type: Object,
      required: true,
    },
    showErrorAlert: {
      type: Boolean,
      default: false,
    },
    errorAlertText: {
      type: String,
      required: true,
    },
  },
  components: {
    Button,
  },
  data() {
    return {
      microsoftIcon,
      googleIcon,
      ssoIcon,
      showSeePassword: false,
      passwordRules: [
        value => !!value || this.$t('auth.please_enter_a_password'),
      ],
      emailRules: [
        email => !!email || this.$t('validation.form.this_field_is_required'),
        email =>
          validationHelper.isValidEmailAddress(email) ||
          this.$t('onboarding.invite.invalid_email_address'),
      ],
      FIELDS,
    }
  },

  methods: {
    onContinueClick() {
      this.account.email = this.account.email.trim()
      this.$emit('onContinueClick')
    },

    onChange(field, data) {
      this.$emit('onChange', field, data)
    },
  },

  computed: {
    isButtonEnabled() {
      return Boolean(this.account.email)
    },
  },
}
</script>

<style lang="scss" scoped>
.accessFirstStep {
  &__text--secondary {
    color: $color-blue-grey;
    font-size: 14px;
    text-align: center;
  }
}
</style>
